import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi } from "@/lib/api/bindery";
import { CostEstimateCard } from "./CostEstimateCard";
import { ThesisDetailsCard } from "./ThesisDetailsCard";
import { ESTIMATE_DEBOUNCE_MS } from "./newThesisConstants";
import { useEditableThesis } from "./useEditableThesis";

export default function EditThesisRequestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { form, isValid, setField, markTouched, markAllTouched, errorFor, ready } =
    useEditableThesis(id);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState<{ unitPrice: number; totalPrice: number } | null>(null);

  const estimateInput = useMemo(
    () => ({
      degreeLevel: form.degreeLevel,
      bindingType: form.bindingType,
      pageCount: Number(form.pageCount) || 0,
      copies: Number(form.copies) || 1,
      colorPages: Number(form.colorPages) || 0,
    }),
    [form.degreeLevel, form.bindingType, form.pageCount, form.copies, form.colorPages]
  );

  useEffect(() => {
    if (!ready || estimateInput.pageCount <= 0) return;
    let active = true;
    const handle = setTimeout(async () => {
      setEstimating(true);
      try {
        const data = await binderyApi.thesis.estimate(estimateInput);
        if (active) {
          setEstimate({ unitPrice: Number(data.unitPrice), totalPrice: Number(data.totalPrice) });
        }
      } catch {
        if (active) setEstimate(null);
      } finally {
        if (active) setEstimating(false);
      }
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [estimateInput, ready]);

  const save = async () => {
    if (!id) return;
    markAllTouched();
    if (!isValid) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSaving(true);
    try {
      await binderyApi.thesis.updateMine(id, {
        degreeLevel: form.degreeLevel,
        title: form.title.trim(),
        supervisor: form.supervisor.trim(),
        pageCount: Number(form.pageCount),
        copies: Number(form.copies),
        bindingType: form.bindingType,
        colorPages: Number(form.colorPages) || 0,
        paperGrade: form.paperGrade.trim() || undefined,
        fileUrl: form.fileUrl.trim(),
        formattingNotes: form.formattingNotes.trim() || undefined,
      });
      toast.success("Request updated");
      navigate(`/my/thesis/${id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update request"));
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Edit thesis request"
        description="You can change this request until the library starts the format check."
        breadcrumbItems={[{ label: "My thesis requests", href: "/my/thesis" }, { label: "Edit" }]}
        showSearch={false}
        titleSize="md"
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ThesisDetailsCard
            form={form}
            setField={setField}
            markTouched={markTouched}
            errorFor={errorFor}
          />
        </div>
        <div className="space-y-4">
          <CostEstimateCard
            estimating={estimating}
            estimate={estimate}
            copies={estimateInput.copies}
          />
          <Button className="w-full" disabled={saving || !isValid} onClick={save}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save changes
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
